import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import api from "../api/client";

let listenersAttached = false;
let swRegistration = null;

/**
 * Synthesizes a pleasant dual-tone water notification chime using Web Audio API
 */
export function playHydrationChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();

    // First tone (587.33 Hz - D5)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(587.33, ctx.currentTime);
    gain1.gain.setValueAtTime(0.3, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.4);

    // Second tone (880 Hz - A5)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.15);
    gain2.gain.setValueAtTime(0.4, ctx.currentTime + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.65);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 0.65);
  } catch (e) {
    console.warn("Audio chime error:", e.message);
  }
}

/**
 * Show System Desktop / Mobile OS Notification Banner
 * (Works even when app tab is inactive or in background)
 */
export function showSystemNotification(title, options = {}) {
  try {
    if (title && (title.includes("Hydration") || title.includes("Water") || options.isHydration)) {
      playHydrationChime();
    }

    const defaultOptions = {
      body: "You have a new update in GoldPE WIBIL.",
      icon: "/favicon.svg",
      badge: "/favicon.svg",
      vibrate: [200, 100, 200, 100, 200],
      requireInteraction: true,
      ...options
    };

    if (swRegistration && swRegistration.active && "showNotification" in swRegistration) {
      swRegistration.showNotification(title, defaultOptions).catch(() => {
        if ("Notification" in window && Notification.permission === "granted") {
          try { new Notification(title, defaultOptions); } catch (e) {}
        }
      });
    } else if ("Notification" in window && Notification.permission === "granted") {
      try { new Notification(title, defaultOptions); } catch (e) {}
    }
  } catch (err) {
    console.error("Error showing system notification:", err);
  }
}

/**
 * Requests notification permissions for both Web Browser and Native Apps.
 * Registers Service Worker for background push popups.
 */
export async function initPush(onNotificationTap) {
  // 1. Web / Desktop Browser Notifications & Service Worker
  if (typeof window !== "undefined" && "serviceWorker" in navigator) {
    try {
      swRegistration = await navigator.serviceWorker.register("/sw.js");
      console.log("✅ Service Worker registered for background notifications.");
    } catch (err) {
      console.warn("Service worker registration failed:", err);
    }
  }

  if (typeof window !== "undefined" && "Notification" in window) {
    if (Notification.permission === "default") {
      try {
        const res = await Notification.requestPermission();
        console.log("Notification permission response:", res);
      } catch (err) {
        console.warn("Notification permission request error:", err);
      }
    }
  }

  // 2. Native Android / iOS Push Notifications (Capacitor)
  if (!Capacitor.isNativePlatform()) return;

  try {
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive !== "granted") {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== "granted") {
      console.warn("Push notification permission was not granted");
      return;
    }

    await PushNotifications.register();

    if (listenersAttached) return;
    listenersAttached = true;

    PushNotifications.addListener("registration", async (token) => {
      try {
        localStorage.setItem("fp_push_token", token.value);
        await api.post("/push/register", {
          token: token.value,
          platform: Capacitor.getPlatform(), // "android" | "ios"
        });
      } catch (err) {
        console.error("Failed to register push token with backend:", err);
      }
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.error("Push registration error:", err);
    });

    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      console.log("Push received in foreground:", notification);
      showSystemNotification(notification.title || "GoldPE Alert", {
        body: notification.body || ""
      });
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      if (onNotificationTap) onNotificationTap(action.notification?.data || {});
    });
  } catch (err) {
    console.error("Native push init error:", err);
  }
}

/** Removes this device's token from the backend. Call on logout. */
export async function unregisterPush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const token = localStorage.getItem("fp_push_token");
    if (token) {
      await api.delete("/push/register", { data: { token } });
      localStorage.removeItem("fp_push_token");
    }
  } catch (err) {
    console.error("Failed to unregister push token:", err);
  }
}
